import { isArray, isNumber, isObject, isString } from 'lodash'
import type { App } from 'vue'

const JSON_PREFIX = '[JSON]:';
const NUMBER_PREFIX = '[NUMBER]:';
const NULL_IDENTIFIER = '[NULL]';

declare module '@vue/runtime-core' {
    interface ComponentCustomProperties {
        $storage: StoragePlugin
        $updateStore: (variable: string, key: string, session?: boolean) => void
    }
}

export class StoragePlugin {

    constructor(protected prefix = '') {
    }

    setLocal(key: string, value: any) {

        this.set(this.getKey(key), value, window.localStorage);
    }

    getLocal(key: string, defaultValue: any = null) {

        return this.get(this.getKey(key), defaultValue, window.localStorage);
    }

    removeLocal(key: string) {

        this.remove(this.getKey(key), window.localStorage);
    }

    setSession(key: string, value: any) {

        this.set(this.getKey(key), value, window.sessionStorage);
    }

    getSession(key: string, defaultValue = null) {

        return this.get(this.getKey(key), defaultValue, window.sessionStorage);
    }

    removeSession(key) {

        this.remove(this.getKey(key), window.sessionStorage);
    }


    protected getKey(key): string {

        return this.prefix + key;
    }

    protected set(key: string, value: any, engine: Storage) {

        let processedValue = value;

        if(value === null){
            processedValue = NULL_IDENTIFIER;
        }
        else if(isObject(value) || isArray(value)){
            processedValue = JSON_PREFIX + JSON.stringify(value);
        }
        else if(isNumber(value)){
            processedValue = NUMBER_PREFIX + value;
        }

        engine.setItem(key, processedValue);
    }

    protected get(key: string, defaultValue: any, engine: Storage): any {

        let value = engine.getItem(key);
        if(value === null){
            return defaultValue;
        }

        const jsonPrefixLength = JSON_PREFIX.length;
        const numberPrefixLength = NUMBER_PREFIX.length;

        let processedValue: any = value;

        // Check null
        if(value === NULL_IDENTIFIER){
            processedValue = null;
        }
        // Check JSON
        else if(isString(value) && value.substring(0, jsonPrefixLength) === JSON_PREFIX){

            const json = value.substring(jsonPrefixLength);
            processedValue = JSON.parse(json);
        }
        // Check Number
        else if(isString(value) && value.substring(0, numberPrefixLength) === NUMBER_PREFIX){

            const number = value.substring(numberPrefixLength);
            processedValue = parseInt(number);
        }

        return processedValue;
    }

    protected remove(key: string, engine: Storage) {

        engine.removeItem(key);
    }

    install(app: App) {

        const plugin = this;
        app.config.globalProperties.$storage = this;

        app.mixin({

            methods: {

                $updateStore: function(variable: string, key: string, session = true) {

                    this.$watch(variable, (value) => {

                        if(session){
                            plugin.setSession(key, value);
                        }
                        else {
                            plugin.setLocal(key, value);
                        }
                    }, { deep: true });
                }
            }
        });
    }
}